import React, { useRef, useContext, useState, useEffect } from 'react'
import { useSignal } from "@preact/signals-react"
import Snap from 'snapsvg-cjs'
import { StoreContext } from "../../context/StoreContext"
import Utility from "../../objects/Utility"
import SheetControls from '../SheetControls/SheetControls'
import SheetOperations from '../SheetOperations/SheetOperations'
import SheetEdit from '../SheetEdit/SheetEdit'
import './Sheet.scss'

const Sheet = ({ layer, setOverlayActive }) => {
    const controller = new AbortController();
    const { signal } = controller;
    const { state, actions } = useContext(StoreContext)
    const svgRef = useRef(null)
    const printableAreaMM = [207.44, 270.94]
    //  8.46457 inches 11 inches
    const sheetDisplayRef = useRef(null)
    const [sheetParameters, setSheetParameters] = useState(null)
    const [sheetMenuControlsOpened, setSheetMenuControlsOpened] = useState(false)
    const [sheetMenuOperationsOpened, setSheetMenuOperationsOpened] = useState(false)
    const [sheetMenuEditOpened, setSheetMenuEditOpened] = useState(false)
    const [sheetSide, setSheetSide] = useState('front')

    const sgPreviousWindowResize = useSignal({ width: -1, height: -1 })

    const sgContextMenuActive = useSignal(null)
    const sgLastSheetParameters = useSignal({})
    const [contextBox, setContextBox] = useState({ top: -1, left: -1 })
    const sgPaintHash = useSignal(null)
    const draggingSlot = useSignal(null)

    const [changingGuideType, setChangingGuideType] = useState(false)
    const [sheetAspectRatioChangedSheetParameters, setSheetAspectRatioChangedSheetParameters] = useState(false)
    const [redoSheet, setRedoSheet] = useState(false)
    //const [sheetEditHiliteSlots, setSheetEditHiliteSlots] = useState([])
    const [sheetEditPaper, setSheetEditPaper] = useState(null)
    const [hilitedSlots, setHilitedSlots] = useState([])
    const [hilitedSlotIndex, setHilitedSlotIndex] = useState(-1)

    useEffect(() => {
        let _paper = setupSheetPaper()
        actions.sheetPaper(_paper)

        const mouseUp = evt => {
            if (evt.target.id.startsWith('sheet_context_menu') === false && sgContextMenuActive.value) {
                let slotNumber = sgContextMenuActive.value.number
                setTimeout(() => {
                    if (sgContextMenuActive.value && sgContextMenuActive.value.number === slotNumber) {
                        sgContextMenuActive.value = null
                    }
                }, 0)
            }
        }
        document.getElementById('sheetContainer').addEventListener("mouseup", mouseUp, { signal });

        // }
    }, []) // eslint-disable-line react-hooks/exhaustive-deps

    const setupSheetPaper = () => {
        let _paper = Snap("#sheetDisplay");
        _paper.attr({ id: "sheetDisplay", width: "100%", height: "100%" });
        return _paper
    }

    useEffect(() => {
        if (state.sheetPaper) {
            if (state.sheetSettings && !Utility.emptyCheck(state.sheetSettings)) {
                if (!sheetParameters) {
                    setupSheetParameters()
                }
            }

        }
    }, [state.sheetPaper]) // eslint-disable-line react-hooks/exhaustive-deps

    const setupSheetParameters = () => {
        let translatedSheetParameters = null
        let useCounterSizeMM = 12.7
        if (!state.sheetSettings.counterSize || state.sheetSettings.counterSize === -1) {
            useCounterSizeMM = 12.7
        }
        else {
            useCounterSizeMM = state.sheetSettings.counterSize
        }
        let useCounterCustomSize = false
        if (state.sheetSettings.useCounterCustomSize && state.sheetSettings.counterCustomSize) {
            useCounterSizeMM = state.sheetSettings.counterCustomSize
            useCounterCustomSize = true
        }

        let usePrintableAreaMM = printableAreaMM // default
        if (state.sheetSettings.printableArea) {
            usePrintableAreaMM = state.sheetSettings.printableArea
        }

        let sheetSizes = constructSheetSizes(useCounterSizeMM, usePrintableAreaMM)
        // sheetSizes domWidth, domHeight, ratioMMtoPixels, counterSizeInPixels, countersColumnsRows
        let sheetSide = 'front'
        if (state.sheetSettings.sheetSide) {
            sheetSide = state.sheetSettings.sheetSide
        }

        let countersLayoutMaxColsRows = computeCountersMaxColsRows(sheetSizes, state.sheetSettings.counterMargins, state.sheetSettings.pageMargins)
        let maxCols = countersLayoutMaxColsRows.cols // defaults to max cols and rows that can fit
        let maxRows = countersLayoutMaxColsRows.rows

        translatedSheetParameters = {
            cols: state.sheetSettings.countersColumnsRows[0],
            rows: state.sheetSettings.countersColumnsRows[1],
            counterSizeInPixels: useCounterSizeMM,
            counterCustomSize: useCounterCustomSize ? useCounterSizeMM : '',
            useCounterCustomSize: useCounterCustomSize,
            counterSizeMM: state.sheetSettings.counterSize,
            width: sheetSizes.domWidth,
            height: sheetSizes.domHeight,
            printableAreaMM: usePrintableAreaMM,
            maxCols: maxCols,
            maxRows: maxRows,
            ratioMMtoPixels: sheetSizes.ratioMMtoPixels,
            counterMargins: state.sheetSettings.counterMargins,
            pageMargins: state.sheetSettings.pageMargins,
            guideType: state.sheetSettings.guideType,
            sheetSide: sheetSide,
        }
        let _sheetParameters = computeSheetParameters(translatedSheetParameters)
        setSheetParameters(_sheetParameters)
    }

    const computeSheetParameters = (useSheetParameters = sheetParameters) => {
        let counterSizeMM = 12.7 // default 
        let counterMargins = [0, 0, 0, 0] // default
        let pageMargins = [0, 0, 0, 0] // default
        let counterCustomSize = '' // default
        let useCounterCustomSize = false // default
        let useGuideType = 'none' // default
        let usePrintableAreaMM = printableAreaMM
        let sheetSide = 'front'

        if (useSheetParameters) {
            if (useSheetParameters.guideType) {
                useGuideType = useSheetParameters.guideType
            }
            if (useSheetParameters.sheetSide) {
                sheetSide = useSheetParameters.sheetSide
            }
            if (useSheetParameters.counterSizeMM) {
                counterSizeMM = useSheetParameters.counterSizeMM
            }
            if (useSheetParameters.counterCustomSize && useSheetParameters.useCounterCustomSize) {
                counterSizeMM = useSheetParameters.counterCustomSize
                counterCustomSize = useSheetParameters.counterCustomSize
                useCounterCustomSize = useSheetParameters.useCounterCustomSize
            }

            if (useSheetParameters.counterMargins) {
                counterMargins = useSheetParameters.counterMargins
            }
            if (useSheetParameters.pageMargins) {
                pageMargins = useSheetParameters.pageMargins
            }
            if (useSheetParameters.printableAreaMM) {
                usePrintableAreaMM = useSheetParameters.printableAreaMM
            }
        }
        let sheetSizes = constructSheetSizes(counterSizeMM, usePrintableAreaMM)
        let countersLayoutMaxColsRows = computeCountersMaxColsRows(sheetSizes, counterMargins, pageMargins)

        let useCols = countersLayoutMaxColsRows.cols // defaults to max cols and rows that can fit
        let useRows = countersLayoutMaxColsRows.rows
        // use existing cols and rows settings, unless they dont fit
        if (useSheetParameters && useSheetParameters.cols && useSheetParameters.rows) {
            if (useSheetParameters.cols < useCols) {
                useCols = useSheetParameters.cols
            }
            if (useSheetParameters.rows < useRows) {
                useRows = useSheetParameters.rows
            }
        }
        return {
            width: sheetSizes.domWidth,
            height: sheetSizes.domHeight,
            printableAreaMM: usePrintableAreaMM,
            counterSizeInPixels: sheetSizes.counterSizeInPixels,
            counterSizeMM: sheetSizes.counterSizeMM,
            counterCustomSize: counterCustomSize,
            useCounterCustomSize: useCounterCustomSize,
            ratioMMtoPixels: sheetSizes.ratioMMtoPixels,
            cols: useCols,
            rows: useRows,
            maxCols: countersLayoutMaxColsRows.cols,
            maxRows: countersLayoutMaxColsRows.rows,
            counterMargins: counterMargins,
            pageMargins: pageMargins,
            guideType: useGuideType,
            sheetSide: sheetSide,
        }
    }

    useEffect(() => {
        if (sheetParameters) {
            if (changingGuideType) {
                actions.sheetSettings({ ...state.sheetSettings, guideType: changingGuideType })
                return
            }
            sgContextMenuActive.value = null
            let slots = generateSlotsData()
            // preserve counterState data for any slots with counters in them.
            if (state.slots.length > 0) {
                slots.forEach(slot => {
                    let sslot = state.slots.find(ss => ss.number === slot.number)
                    if (sslot) {
                        if (sslot.counterState && !Utility.emptyCheck(sslot.counterState)) {
                            slot.counterState = { ...sslot.counterState }
                        }
                    }
                })
            }
            actions.slots(slots)
            let _sheetSettings = {
                counterCustomSize: sheetParameters.counterSizeMM,
                counterMargins: sheetParameters.counterMargins,
                pageMargins: sheetParameters.pageMargins,
                counterSize: sheetParameters.counterSizeMM,
                useCounterCustomSize: sheetParameters.useCounterCustomSize,
                countersColumnsRows: [sheetParameters.cols, sheetParameters.rows],
                printableArea: sheetParameters.printableAreaMM,
                guideType: sheetParameters.guideType,
                sheetSide: sheetParameters.sheetSide,
            }
            actions.sheetSettings(_sheetSettings)
        }
    }, [sheetParameters]) // eslint-disable-line react-hooks/exhaustive-deps

    const generateSlotsData = () => {
        let obj = {}
        let slots = []
        // 'number', 'xy', 'centerXy', 'pixelsWidth', 'position', 'counterState'
        let count = 0
        let cols = sheetParameters.cols
        let rows = sheetParameters.rows
        let counterSizePX = sheetParameters.counterSizeInPixels
        let ratioMMtoPixels = sheetParameters.ratioMMtoPixels
        let counterMargins = sheetParameters.counterMargins
        let pageMargins = sheetParameters.pageMargins
        let marginLeftPixels = counterMargins[3] * ratioMMtoPixels
        let marginTopPixels = counterMargins[0] * ratioMMtoPixels
        let marginRightPixels = counterMargins[1] * ratioMMtoPixels
        let marginBottomPixels = counterMargins[2] * ratioMMtoPixels
        let pageLeftPixels = pageMargins[3] * ratioMMtoPixels
        let pageTopPixels = pageMargins[0] * ratioMMtoPixels

        let totalCounterPixelsWidth = sheetParameters.counterSizeInPixels + marginLeftPixels + marginRightPixels
        let totalCounterPixelsHeight = sheetParameters.counterSizeInPixels + marginTopPixels + marginBottomPixels
        for (let row = 0; row < rows; row++) {
            for (let col = 0; col < cols; col++) {
                obj.number = count
                obj.rearNumber = (cols - col - 1) + (row * cols)
                count++
                obj.xy = { x: col * totalCounterPixelsWidth, y: row * totalCounterPixelsHeight }
                obj.xy.x += marginLeftPixels + pageLeftPixels;
                obj.xy.y += marginTopPixels + pageTopPixels
                obj.centerXy = { x: obj.xy.x + (counterSizePX / 2), y: obj.xy.y + (counterSizePX / 2) }
                obj.pixelsWidth = counterSizePX // not sure I need this field. I can compute this later if any counter gets loaded into this slot.
                obj.position = { col, row }
                obj.rearPosition = { col: cols - col - 1, row }
                obj.counterState = null
                slots.push({ ...obj })
            }
        }

        // the max counters that can fit on a sheet is (not valid, not sure where i'll put the cuttoff yet) 373, if the user chooses 1/2 inch counters.
        // however, if they fill up the sheet, then choose a bigger counter size, the counters
        // towards the end may not have a slot to be painted on. For these, we set the xy values
        // to -1, but still retain the counter data, incase they later reduce the counter size.
        if (count < 1609) {
            for (count; count <= 1609; count++) {
                obj.number = count
                obj.xy = { x: -1, y: -1 }
                obj.centerXy = { x: -1, y: -1 }
                obj.pixelsWidth = -1 // not sure I need this field. I can compute this later if any counter gets loaded into this slot.
                obj.position = { col: -1, row: -1 }
                obj.counterState = null
                slots.push({ ...obj })
            }
        }

        return slots
    }

    useEffect(() => {
        if (state.slots.length > 0) {
            if (state.sheetPaper === null) {
                let _paper = setupSheetPaper()
                actions.sheetPaper(_paper)
                return
            }
            if (state.sheetPaper && sheetParameters) {
                if (changingGuideType) {
                    manageGuideType()
                    setChangingGuideType(false)
                    return
                }
                // hash can't handle huge strings, so we gotta reduce the size of state.slots, cause that can be huge.
                let slotsString = ''
                state.slots.forEach((ss, index) => {
                    if (ss.counterState && ss.counterState.package) {
                        if (ss.counterState.package.counterSideActive.front === undefined) {
                            slotsString += 'none'
                        }
                        else {
                            slotsString += ss.counterState.package.counterSideActive.front.substring(0, 100)
                        }
                    }
                    else {
                        slotsString += 'none'
                    }
                })
                let hash = Utility.hashCode(slotsString + JSON.stringify(sheetParameters))
                if (hash === sgPaintHash.v) {
                    return
                }
                sgPaintHash.v = hash
                paintSheet()
                setRedoSheet(true)
            }
        }
    }, [state.slots, state.sheetSettings]) // eslint-disable-line react-hooks/exhaustive-deps

    const paintSheet = () => {
        if (state.sheetPaper) {
            state.sheetPaper.clear()
            if (sheetEditPaper) {
                sheetEditPaper.clear()
            }
            paintBackground()
            paintSlots(sheetSide)
            paintCuttingGuides()
            manageGuideType()
            paintCounters()
            if (sheetEditPaper) {
                sheetEditPaper.clear()
                if (hilitedSlots.length > 0) {
                    hiliteSlots(hilitedSlots)
                    if (hilitedSlotIndex > -1) {
                        hiliteTargetSlotIndex(hilitedSlotIndex)
                    }
                }
            }
        }
    }

    const paintBackground = () => {
        if (!sheetParameters || !sheetParameters.pageMargins) {
            console.error('no sheetParameters:', sheetParameters)
            return
        }
        let pageMargins = sheetParameters.pageMargins
        let ratioMMtoPixels = sheetParameters.ratioMMtoPixels
        let pageWidth = sheetParameters.width
        let pageHeight = sheetParameters.height
        let pageLeftPixels = pageMargins[3] * ratioMMtoPixels
        let pageTopPixels = pageMargins[0] * ratioMMtoPixels
        let pageRightPixels = pageMargins[1] * ratioMMtoPixels
        let pageBottomPixels = pageMargins[2] * ratioMMtoPixels




        const p = state.sheetPaper.path("M 0 0 L 25 0 25 25 0 25 M 25 25 L 50 25 50 50 25 50 25 25").attr({
            stroke: "#000",
            strokeWidth: 0,
            fill: "#aaa"
        }).toPattern(0, 0, 50, 50)

        p.attr({ "data-type": "pageMargin" })

        state.sheetPaper.rect(pageLeftPixels, pageTopPixels,
            pageWidth - (pageLeftPixels + pageRightPixels),
            pageHeight - (pageTopPixels + pageBottomPixels)).attr({ fill: "#bbb", "data-type": "pageMargin" })

        state.sheetPaper.rect(pageLeftPixels, pageTopPixels,
            pageWidth - (pageLeftPixels + pageRightPixels),
            pageHeight - (pageTopPixels + pageBottomPixels)).attr({ fill: p, "data-type": "pageMargin" })
        //state.sheetPaper.paper.rect(pageLeftPixels, pageTopPixels, pageWidth - (pageLeftPixels + pageRightPixels), pageHeight - (pageTopPixels + pageBottomPixels)).attr({ id: 'pageMarginTop', stroke: "none", "strokeWidth": 0, fill: p, "data-type": "pageMargin" })
        // let d="M 0 0 L 50 0 50 50 0 50 M 50 50 L 100 50 100 100 50 100 50 50"
        // state.sheetPaper.path(d).attr({stroke: "red", fill: "red", strokeWidth: 1}) 
    }

    const paintCuttingGuides = () => {
        let corners = []
        let currentCuttingGuide = ''
        // let cols = state.sheetSettings.countersColumnsRows[0]
        // let rows = state.sheetSettings.countersColumnsRows[1]
        let cols = sheetParameters.cols
        let rows = sheetParameters.rows

        const drawLines = segments => {
            let linesDrawn = []
            segments.forEach(sg => {
                let x1 = sg.from.leftSide.x
                let y1 = sg.from.leftSide.y
                let x2 = sg.to.leftSide.x
                let y2 = sg.to.leftSide.y
                let path = 'M ' + x1 + ' ' + y1 + ' L ' + x2 + ' ' + y2
                if (!linesDrawn.includes(path)) {
                    linesDrawn.push(path)
                    state.sheetPaper.path(path).attr({
                        stroke: "black", strokeWidth: 2, fill: "none",
                        "data-type": "cuttingGuide" + currentCuttingGuide
                    })
                }
                x1 = sg.from.rightSide.x
                y1 = sg.from.rightSide.y
                x2 = sg.to.rightSide.x
                y2 = sg.to.rightSide.y
                path = 'M ' + x1 + ' ' + y1 + ' L ' + x2 + ' ' + y2
                if (!linesDrawn.includes(path)) {
                    linesDrawn.push(path)
                    state.sheetPaper.path(path).attr({
                        stroke: "black", strokeWidth: 2, fill: "none",
                        "data-type": "cuttingGuide" + currentCuttingGuide
                    })
                }
            })
        }

        const drawHLines = segments => {
            let linesDrawn = []
            segments.forEach(sg => {
                let x1 = sg.from.topSide.x
                let y1 = sg.from.topSide.y
                let x2 = sg.to.topSide.x
                let y2 = sg.to.topSide.y
                let path = 'M ' + x1 + ' ' + y1 + ' L ' + x2 + ' ' + y2
                if (!linesDrawn.includes(path)) {
                    linesDrawn.push(path)
                    state.sheetPaper.path(path).attr({
                        stroke: "black", strokeWidth: 2, fill: "none",
                        "data-type": "cuttingGuide" + currentCuttingGuide
                    })
                }
                x1 = sg.from.bottomSide.x
                y1 = sg.from.bottomSide.y
                x2 = sg.to.bottomSide.x
                y2 = sg.to.bottomSide.y
                path = 'M ' + x1 + ' ' + y1 + ' L ' + x2 + ' ' + y2
                if (!linesDrawn.includes(path)) {
                    linesDrawn.push(path)
                    state.sheetPaper.path(path).attr({
                        stroke: "black", strokeWidth: 2, fill: "none",
                        "data-type": "cuttingGuide" + currentCuttingGuide
                    })
                }
            })
        }

        // also used for edges
        const drawCorners = corners => {
            let x = -1
            let y = -1
            let dLR = sheetParameters.counterMargins[1] + sheetParameters.counterMargins[3]
            dLR = Utility.roundFloat(dLR, 1)
            dLR = dLR > 15 ? 15 : dLR
            dLR = dLR < 2 ? 2 : dLR
            let dTB = sheetParameters.counterMargins[0] + sheetParameters.counterMargins[2]
            dTB = Utility.roundFloat(dTB, 1)
            dTB = dTB > 15 ? 15 : dTB
            dTB = dTB < 2 ? 2 : dTB

            corners.forEach((corner, index) => {
                x = Utility.roundFloat(corner.x, 1)
                y = Utility.roundFloat(corner.y, 1)
                if (x !== 99999999 && x > -1 && y > -1) {
                    let path = ''
                    switch (index) {
                        case 0: // left top
                            path = `M${x - dLR} ${y} L ${x} ${y} ${x} ${y - dTB}`
                            break
                        case 1: // right top
                            path = `M${x} ${y - dTB} L ${x} ${y} ${x + dLR} ${y}`
                            break
                        case 2: // bottom right
                            path = `M${x + dLR} ${y} L ${x} ${y} ${x} ${y + dTB}`
                            break
                        case 3: // bottom left
                            path = `M${x - dLR} ${y} L ${x} ${y} ${x} ${y + dTB}`
                            break
                        default: path = ''
                    }
                    if (path !== '') {
                        state.sheetPaper.path(path).attr({
                            stroke: "black", "stroke-width": 2, fill: "none",
                            "data-type": "cuttingGuide" + currentCuttingGuide
                        })
                    }
                }
            })
        }



        // remove previous guidelines, if any
        // draw new cutting guides
        currentCuttingGuide = 'Corners'
        state.slots.forEach(sl => {
            if (sl.xy.x > -1) {
                let corners = []
                let x = sl.xy.x
                let y = sl.xy.y
                corners.push({ x, y })
                x = x + sl.pixelsWidth
                corners.push({ x, y })
                y = y + sl.pixelsWidth
                corners.push({ x, y })
                x = sl.xy.x
                corners.push({ x, y })
                drawCorners(corners)
            }
        })

        currentCuttingGuide = 'Edges'

        corners = []

        // remove previous guidelines, if any
        // draw new cutting guides
        state.slots.forEach(sl => {
            if (sl.xy.x > -1) {

                if (sl.position.col === 0 && sl.position.row === 0) {
                    corners = []
                    let x = sl.xy.x
                    let y = sl.xy.y
                    corners.push({ x, y })
                    x = x + sl.pixelsWidth
                    corners.push({ x, y })
                    y = y + sl.pixelsWidth
                    corners.push({ x: -1, y: -1 })
                    x = sl.xy.x
                    corners.push({ x, y })
                    drawCorners(corners)
                }
                if (sl.position.col > 0 && sl.position.col < cols - 1 && sl.position.row === 0) {
                    corners = []
                    let x = sl.xy.x
                    let y = sl.xy.y
                    corners.push({ x, y })
                    x = x + sl.pixelsWidth
                    corners.push({ x, y })
                    y = y + sl.pixelsWidth
                    corners.push({ x: -1, y: -1 })
                    x = sl.xy.x
                    corners.push({ x: -1, y: -1 })
                    drawCorners(corners)
                }
                if (sl.position.col === cols - 1 && sl.position.row === 0) {
                    corners = []
                    let x = sl.xy.x
                    let y = sl.xy.y
                    corners.push({ x, y })
                    x = x + sl.pixelsWidth
                    corners.push({ x, y })
                    y = y + sl.pixelsWidth
                    corners.push({ x, y })
                    x = sl.xy.x
                    corners.push({ x: -1, y: -1 })
                    drawCorners(corners)
                }

                if (sl.position.col === 0 && sl.position.row > 0 && sl.position.row < rows - 1) {
                    corners = []
                    let x = sl.xy.x
                    let y = sl.xy.y
                    corners.push({ x, y })
                    x = x + sl.pixelsWidth
                    corners.push({ x: -1, y: -1 })
                    y = y + sl.pixelsWidth
                    corners.push({ x: -1, y: -1 })
                    x = sl.xy.x
                    corners.push({ x, y })
                    drawCorners(corners)
                }
                if (sl.position.col === cols - 1 && sl.position.row > 0 && sl.position.row < rows - 1) {
                    corners = []
                    let x = sl.xy.x
                    let y = sl.xy.y
                    corners.push({ x: 99999999, y: 99999999 })
                    x = x + sl.pixelsWidth
                    corners.push({ x, y })
                    y = y + sl.pixelsWidth
                    corners.push({ x, y })
                    x = sl.xy.x
                    corners.push({ x: 99999999, y: 99999999 })
                    drawCorners(corners)
                }
                if (sl.position.col === cols - 1 && sl.position.row === rows - 1) {
                    corners = []
                    let x = sl.xy.x
                    let y = sl.xy.y
                    corners.push({ x: -1, y: -1 })
                    x = x + sl.pixelsWidth
                    corners.push({ x, y })
                    y = y + sl.pixelsWidth
                    corners.push({ x, y })
                    x = sl.xy.x
                    corners.push({ x, y })
                    drawCorners(corners)
                }
                if (sl.position.col === 0 && sl.position.row === rows - 1) {
                    corners = []
                    let x = sl.xy.x
                    let y = sl.xy.y
                    corners.push({ x, y })
                    x = x + sl.pixelsWidth
                    corners.push({ x: -1, y: -1 })
                    y = y + sl.pixelsWidth
                    corners.push({ x, y })
                    x = sl.xy.x
                    corners.push({ x, y })
                    drawCorners(corners)
                }
                if (sl.position.col > 0 && sl.position.col < cols - 1 && sl.position.row === rows - 1) {
                    corners = []
                    let x = sl.xy.x
                    let y = sl.xy.y
                    corners.push({ x: -1, y: -1 })
                    x = x + sl.pixelsWidth
                    corners.push({ x: -1, y: -1 })
                    y = y + sl.pixelsWidth
                    corners.push({ x, y })
                    x = sl.xy.x
                    corners.push({ x, y })
                    drawCorners(corners)
                }

            }
        })

        currentCuttingGuide = 'Lines'
        let segmentsVertical = []
        let segmentsHorizontal = []
        //let dM = state.sheetSettings.counterMargins[1] + state.sheetSettings.counterMargins[3]
        let dM = sheetParameters.counterMargins[1] + sheetParameters.counterMargins[3]
        dM = Utility.roundFloat(dM, 1)
        dM = dM < 2 ? 2 : dM

        for (let c = 0; c < cols; c++) {
            segmentsVertical.push(
                {
                    from: {
                        leftSide: {
                            col: c, row: 0, x: null, y: null
                        },
                        rightSide: {
                            col: c, row: 0, x: null, y: null
                        }
                    },
                    to: {
                        leftSide: {
                            col: c, row: (rows - 1), x: null, y: null
                        },
                        rightSide: {
                            col: c, row: (rows - 1), x: null, y: null
                        }
                    }
                }
            )
        }

        for (let r = 0; r < rows; r++) {
            segmentsHorizontal.push(
                {
                    from: {
                        topSide: {
                            col: 0, row: r, x: null, y: null
                        },
                        bottomSide: {
                            col: 0, row: r, x: null, y: null
                        }
                    },
                    to: {
                        topSide: {
                            col: (cols - 1), row: r, x: null, y: null
                        },
                        bottomSide: {
                            col: (cols - 1), row: r, x: null, y: null
                        }
                    }
                }
            )
        }

        state.slots.forEach(sl => {
            if (sl.xy.x > -1) {

                let fromSegment = segmentsVertical.find(sh => sh.from.leftSide.col === sl.position.col &&
                    sh.from.leftSide.row === sl.position.row)
                if (fromSegment) {
                    fromSegment.from.leftSide.x = sl.xy.x
                    fromSegment.from.leftSide.y = sl.xy.y - dM
                    fromSegment.from.rightSide.x = sl.xy.x + sl.pixelsWidth
                    fromSegment.from.rightSide.y = sl.xy.y - dM
                }
                let toSegment = segmentsVertical.find(sh => sh.to.leftSide.col === sl.position.col &&
                    sh.to.leftSide.row === sl.position.row)
                if (toSegment) {
                    toSegment.to.leftSide.x = sl.xy.x
                    toSegment.to.leftSide.y = sl.xy.y + sl.pixelsWidth + dM
                    toSegment.to.rightSide.x = sl.xy.x + sl.pixelsWidth
                    toSegment.to.rightSide.y = sl.xy.y + sl.pixelsWidth + dM
                }

                fromSegment = segmentsHorizontal.find(sh => sh.from.topSide.col === sl.position.col &&
                    sh.from.topSide.row === sl.position.row)
                if (fromSegment) {
                    fromSegment.from.topSide.x = sl.xy.x - dM
                    fromSegment.from.topSide.y = sl.xy.y
                    fromSegment.from.bottomSide.x = sl.xy.x - dM
                    fromSegment.from.bottomSide.y = sl.xy.y + sl.pixelsWidth
                }
                toSegment = segmentsHorizontal.find(sh => sh.to.bottomSide.col === sl.position.col &&
                    sh.to.bottomSide.row === sl.position.row)
                if (toSegment) {
                    toSegment.to.topSide.x = sl.xy.x + dM + sl.pixelsWidth
                    toSegment.to.topSide.y = sl.xy.y
                    toSegment.to.bottomSide.x = sl.xy.x + dM + sl.pixelsWidth
                    toSegment.to.bottomSide.y = sl.xy.y + sl.pixelsWidth
                }

            }
        })
        segmentsVertical = segmentsVertical.filter(sg => sg.from.leftSide.x !== null && sg.from.rightSide.x !== null && sg.from.leftSide.y !== null && sg.from.rightSide.y !== null)
        segmentsHorizontal = segmentsHorizontal.filter(sg => sg.from.topSide.x !== null && sg.from.topSide.x !== null && sg.from.bottomSide.y !== null && sg.from.bottomSide.y !== null)
        drawLines(segmentsVertical)
        drawHLines(segmentsHorizontal)
    }

    const paintCounters = () => {
        let slotsToPaint = state.slots.filter(ssa => ssa.counterState !== null && ssa.xy.x !== -1)
        // clear slots  
        state.slots.forEach(sup => {
            let possibleGroupId = 'group_' + sup.number
            let foundGroup = state.sheetPaper.select("#" + possibleGroupId);
            while (foundGroup) {
                foundGroup.remove()
                foundGroup = state.sheetPaper.select("#" + possibleGroupId);
            }
        })
        slotsToPaint.forEach(stp => paintCounter(stp))
    }

    const constructSheetSizes = (useCounterSizeMM, printableAreaMM) => {
        let domWidth = -1
        let domHeight = -1
        let counterSizeInPixels = -1
        let ratioMMtoPixels = -1
        let sheetDisplayEle = document.getElementById('sheetDisplayContainer')
        if (sheetDisplayEle) {
            const sheetBbox = sheetDisplayEle.getBoundingClientRect()
            let ratio = printableAreaMM[0] / printableAreaMM[1]
            sheetDisplayEle.style.aspectRatio = ratio
            domWidth = sheetBbox.width
            domHeight = sheetBbox.height
            ratioMMtoPixels = domWidth / printableAreaMM[0]
            counterSizeInPixels = Utility.roundFloat(useCounterSizeMM * ratioMMtoPixels, 10)

            return {
                domWidth,
                domHeight,
                counterSizeInPixels,
                counterSizeMM: useCounterSizeMM,
                ratioMMtoPixels
            }
        }
        console.warn('could not get reference to sheetDisplayContainer in Dom')
        return null
    }

    const nextAvailableSlot = () => {
        const { sheetSide } = state.sheetSettings; // Access sheetSide from state
        if (!sheetSide || sheetSide === 'front') {
            return state.slots.find(ls => ls.counterState === null);
        } else {
            let orderedSlots = JSON.parse(JSON.stringify(state.slots));
            orderedSlots.sort((a, b) => a.rearNumber - b.rearNumber);
            let slot = orderedSlots.find(ls => ls.counterState === null);
            let realSlot = state.slots.find(sl => sl.number === slot.number);
            return realSlot;
        }
    };


    useEffect(() => {
        // slots: ['number', 'number', 'xy', 'centerXy', 'pixelsWidth', 'position', 'counterState']
        if (state.moveCounterToSheet) {
            //let freeSlot = state.slots.find(ls => ls.counterState === null)
            let freeSlot = nextAvailableSlot()
            if (freeSlot) {
                freeSlot.counterState = state.moveCounterToSheet
                freeSlot.counterState.package.counterSideActive.active = sheetSide
                actions.slots([...state.slots])
            }
        }
    }, [state.moveCounterToSheet]) // eslint-disable-line react-hooks/exhaustive-deps

    const popupContextMenuForCounterAtSlot = slot => {
        // if clicking again on the same counter, assume the user wants to make the menu go away.
        if (sgContextMenuActive.value !== null) {
            if (sgContextMenuActive.value.number === slot.number) {
                sgContextMenuActive.value = null
                return
            }
        }

        sgContextMenuActive.value = slot
        positionContextMenu(slot.xy.x, slot.xy.y)
    }

    const positionContextMenu = (x, y) => {
        let posX = x + sheetParameters.counterSizeInPixels
        let winWidth = window.innerWidth
        if (state.layoutInline) {
            let elements = document.getElementsByClassName("table-column");
            if (elements && elements.length > 0) {
                let element = elements[0]
                let rect = element.getBoundingClientRect();
                let testX = posX + rect.width
                if (testX + 90 > winWidth) {
                    posX = x - 96
                }
            }
        }
        else {
            // position to the left if its partially offwindow.
            if (posX + 90 > winWidth) {
                posX = x - 96
            }
        }
        setContextBox({ left: posX + 'px', top: y + 'px' })
    }

    const dragStartCounter = evt => {
        let groupId = evt.node.id
        let g = state.sheetPaper.select("#" + groupId);
        g.remove()
        state.sheetPaper.append(g) // make it last painted so it stays "above" the other groups while dragging around.


        let counterNumber = Number(groupId.replace('group_', ''))
        let slots = [...state.slots]
        let movedCounterSlot = slots.find(ss => ss.number === counterNumber)
        if (movedCounterSlot) {
            draggingSlot.value = { g, slot: movedCounterSlot, overRect: null, dragged: false }
        }
    }

    var dragMoveCounter = (evt, dx, dy) => {
        if (!draggingSlot.value) {
            return
        }
        sgContextMenuActive.value = null
        draggingSlot.value.dragged = true
        // let g = state.sheetPaper.select("#" + evt.node.id);
        // g.remove()
        // state.sheetPaper.append(g) // make it last painted so it stays "above" the other groups while dragging around.
        //let overSlot = closestSlotToPoint(slots, { x: centerX, y: centerY })
        let g = draggingSlot.value.g
        let currentMatrix = g.transform().localMatrix;
        let movedX = currentMatrix.e
        let movedY = currentMatrix.f
        let centerX = (movedX + (sheetParameters.counterSizeInPixels / 2)) // sheetParameters.ratioMMtoPixels )
        let centerY = (movedY + (sheetParameters.counterSizeInPixels / 2)) // sheetParameters.ratioMMtoPixels )
        let candidateSlot = closestSlotToPoint(state.slots, { x: centerX, y: centerY })
        let rectId = '#rect_' + candidateSlot.number
        let rectEle = state.sheetPaper.select(rectId)
        if (rectEle) {
            if (draggingSlot.value.overRect) {
                if (draggingSlot.value.overRect !== rectEle) {
                    draggingSlot.value.overRect.attr({ fill: "#eee" }) // remove highlight, this slot is not being dragged over anymore.
                }
            }
            if (candidateSlot.counterState) {
                if (candidateSlot.number === draggingSlot.value.slot.number) {
                    rectEle.attr({ fill: "#f00" }) // if back at original slot
                }
            } else {
                rectEle.attr({ fill: "#ccf" }) // if over an empty slot
            }
            draggingSlot.value.overRect = rectEle
        }
    }

    const dragStopCounter = evt => {
        if (!draggingSlot.value) {
            return;
        }

        if (!draggingSlot.value.dragged) {
            popupContextMenuForCounterAtSlot(draggingSlot.value.slot);
            draggingSlot.value = null;
            return;
        }

        resetSlotHighlight(draggingSlot.value.slot.number);

        const { movedX, movedY, movedCounterSlot } = getMovedCounterDetails(evt);
        //state.sheetPaper.circle(movedX, movedY, sheetParameters.counterSizeInPixels / 4).attr({ fill: "none", stroke: "#000", "stroke-width": 1 });

        const centerX = movedX + (sheetParameters.counterSizeInPixels / 2);
        const centerY = movedY + (sheetParameters.counterSizeInPixels / 2);
        //state.sheetPaper.circle(centerX, centerY, sheetParameters.counterSizeInPixels / 5).attr({ fill: "none", stroke: "#f00", "stroke-width": 1 });

        const candidateSlot = closestSlotToPoint(state.slots, { x: centerX, y: centerY });

        if (candidateSlot) {
            handleCandidateSlot(candidateSlot, movedCounterSlot, evt.node.id);
        } else {
            console.warn('Could not find a slot that was close to x:', centerX, 'y:', centerY);
        }

        draggingSlot.value = null;
    };

    const resetSlotHighlight = slotNumber => {
        const rectId = `#rect_${slotNumber}`;
        const rectEleHome = state.sheetPaper.select(rectId);
        rectEleHome.attr({ fill: "#eee" });
    };

    const getMovedCounterDetails = evt => {
        const groupId = evt.node.id;
        const counterNumber = Number(groupId.replace('group_', ''));
        const g = state.sheetPaper.select(`#${groupId}`);
        const currentMatrix = g.transform().localMatrix;
        const movedX = currentMatrix.e;
        const movedY = currentMatrix.f;
        const slots = [...state.slots];
        const movedCounterSlot = slots.find(ss => ss.number === counterNumber);

        return { movedX, movedY, movedCounterSlot };
    };

    const handleCandidateSlot = (candidateSlot, movedCounterSlot, nodeId) => {
        const g = state.sheetPaper.select(`#${nodeId}`);
        if (candidateSlot.counterState) {
            sendCounterBackHome(g, movedCounterSlot);
        } else {

            moveCounterToSlot(candidateSlot, movedCounterSlot);
        }
    };

    const sendCounterBackHome = (g, movedCounterSlot) => {
        const backHomeMatrix = new Snap.Matrix();
        backHomeMatrix.translate(movedCounterSlot.xy.x, movedCounterSlot.xy.y);
        g.transform(backHomeMatrix);
    };

    const moveCounterToSlot = (candidateSlot, movedCounterSlot) => {
        candidateSlot.counterState = { ...movedCounterSlot.counterState };
        movedCounterSlot.counterState = null;
        let indexCandidateSlot = state.slots.findIndex(ss => ss.number === candidateSlot.number);
        let indexMovedCounterSlot = state.slots.findIndex(ss => ss.number === movedCounterSlot.number);
        let newSlots = [...state.slots];
        newSlots[indexCandidateSlot] = candidateSlot;
        newSlots[indexMovedCounterSlot] = movedCounterSlot;
        actions.slots(newSlots);
    };

    const closestSlotToPoint = (slots, point) => {
        if (!slots || slots.length === 0) {
            return null;
        }

        let arr = slots.map(ss => {
            let distance = Utility.distanceTwoPoints(ss.centerXy, point);
            return { slotNumber: ss.number, distance };
        });

        if (arr.length === 0) {
            return null;
        }

        arr = arr.sort((a, b) => {
            if (a.distance > b.distance) {
                return 1;
            }
            if (a.distance < b.distance) {
                return -1;
            }
            return 0;
        });

        return slots.find(ss => ss.number === arr[0].slotNumber);
    };

    const paintCounter = slot => {
        if (state.sheetPaper === 'reset') {
            return
        }
        if (!sheetParameters || !slot.counterState) {
            return;
        }
        let buttonPressed = -1
        //let svg = slot.counterState[sheetSide === 'front' ? 'frontSvg' : 'rearSvg']
        let svg = ''
        if (sheetSide === 'front') {
            svg = slot.counterState.frontSvg
        }
        else {
            svg = slot.counterState.rearSvg
        }
        if (!svg) {
            console.warn('check custom svg: ', slot.counterState)
            return
        }
        let idStartPos = svg.indexOf('id="combined_')
        let idEndPos = svg.indexOf('" ', idStartPos)
        let idStr = svg.substring(idStartPos + 4, idEndPos)
        svg = svg.replace(idStr, 'slot_' + slot.number)
        // from v2 version, the incoming slots were set at 1000,1000
        svg = svg.replace('width="1000" height="1000"', 'width="' + sheetParameters.counterSizeInPixels + '" height="' + sheetParameters.counterSizeInPixels + '"')
        // for new version, they come in with height width at 100%
        svg = svg.replace('width="100%" height="100%"', 'width="' + sheetParameters.counterSizeInPixels + '" height="' + sheetParameters.counterSizeInPixels + '"')
        let parsed = Snap.parse(svg)
        if (parsed) {
            let g = state.sheetPaper.group()
            g.append(parsed)
            g.attr({ id: 'group_' + slot.number })

            let currentMatrix = g.transform().localMatrix;
            let addMatrix = new Snap.Matrix()
            addMatrix.translate(slot.xy.x, slot.xy.y)
            addMatrix.add(currentMatrix)
            g.transform(addMatrix)

            var move = function (dx, dy) {
                if (buttonPressed !== 0) {
                    return
                }
                this.attr({
                    transform: this.data('origTransform') + (this.data('origTransform') ? "T" : "t") + [dx, dy]
                });
                dragMoveCounter(this, dx, dy)
            }

            // var move = function (dx, dy) {
            //     dragMoveCounter(this, dx, dy)
            // }

            var start = function (x, y, evt) {
                if (evt.button !== 0) {
                    return // only operate on left clicks. Leave right click to browser's use.
                }
                buttonPressed = 0
                this.data('origTransform', this.transform().local);
                dragStartCounter(this)
            }
            var stop = function () {
                if (buttonPressed !== 0) {
                    return
                }
                dragStopCounter(this)
            }

            g.drag(move, start, stop);

        }
    }

    // Throttle function to limit the rate at which a function can fire.
    function throttle(func, wait) {
        let waiting = false;
        return function () {
            if (waiting) {
                return;
            }
            waiting = true;
            setTimeout(() => {
                func.apply(this, arguments);
                waiting = false;
            }, wait);
        };
    }

    useEffect(() => {
        // since windowResize may be emitted with the same values, we need to ignore it when the values are the same.
        if (state.windowResize.width === sgPreviousWindowResize.v.width &&
            state.windowResize.height === sgPreviousWindowResize.v.height) {
            return;
        }

        sgPreviousWindowResize.v.width = state.windowResize.width;
        sgPreviousWindowResize.v.height = state.windowResize.height;

        if (state.windowResize && state.windowResize.width > -1) {
            sgContextMenuActive.value = null;
            if (state.sheetPaper) {
                throttle(evt => {
                    let _sheetParameters = computeSheetParameters(sheetParameters);
                    if (sgLastSheetParameters.v.width !== _sheetParameters.width ||
                        sgLastSheetParameters.v.height !== _sheetParameters.height ||
                        sgLastSheetParameters.v.counterSizeInPixels !== _sheetParameters.counterSizeInPixels ||
                        sgLastSheetParameters.v.cols !== _sheetParameters.cols ||
                        sgLastSheetParameters.v.rows !== _sheetParameters.rows
                    ) {
                        setSheetParameters(_sheetParameters);
                        sgLastSheetParameters.value = { ..._sheetParameters };
                    }
                }, 1000)();
            }
        }
    }, [state.windowResize]); // eslint-disable-line react-hooks/exhaustive-deps


    const computeCountersMaxColsRows = (settings, counterMargins, pageMargins) => {
        let { domWidth, domHeight, ratioMMtoPixels, counterSizeInPixels } = settings;
        let marginLeftPixels = counterMargins[3] * ratioMMtoPixels;
        let marginTopPixels = counterMargins[0] * ratioMMtoPixels;
        let marginRightPixels = counterMargins[1] * ratioMMtoPixels;
        let marginBottomPixels = counterMargins[2] * ratioMMtoPixels;
        let pageLeftPixels = pageMargins[3] * ratioMMtoPixels;
        let pageTopPixels = pageMargins[0] * ratioMMtoPixels;
        let pageRightPixels = pageMargins[1] * ratioMMtoPixels;
        let pageBottomPixels = pageMargins[2] * ratioMMtoPixels;

        domWidth -= (pageLeftPixels + pageRightPixels);
        domHeight -= (pageTopPixels + pageBottomPixels);

        let totalCounterPixelsWidth = counterSizeInPixels + marginLeftPixels + marginRightPixels;
        let totalCounterPixelsHeight = counterSizeInPixels + marginTopPixels + marginBottomPixels;

        let maxColumnCount = Math.floor(domWidth / totalCounterPixelsWidth);
        let maxRowCount = Math.floor(domHeight / totalCounterPixelsHeight);

        return { cols: maxColumnCount, rows: maxRowCount };
    };

    useEffect(() => {
        let _paper = setupSheetPaper()
        actions.sheetPaper(_paper)
    }, [state.layoutInline]) // eslint-disable-line react-hooks/exhaustive-deps

    const paintSlots = sheetSide => {
        if (!state.sheetPaper || !sheetParameters) {
            return
        }

        let paper = state.sheetPaper
        let sheetBbox = null
        // assume the screen width is 8 inches
        let sheetDisplayEle = document.getElementById('sheetDisplayContainer')
        if (sheetDisplayEle) {
            sheetBbox = sheetDisplayEle.getBoundingClientRect()
        }
        if (!sheetBbox) {
            console.warn('no sheetDisplayEle:', sheetDisplayEle)
            return
        }

        let counterSizePX = sheetParameters.counterSizeInPixels
        state.slots.forEach((sl, index) => {
            let x = sl.xy.x
            let y = sl.xy.y
            if (x > -1 && y > -1) { // only for slots that fit on the sheet.
                let cx = sl.centerXy.x
                let cy = sl.centerXy.y

                let backgroundColor = '#ddebdd'
                if (!sheetSide || sheetSide === 'front') {
                    backgroundColor = '#eee'
                }

                paper.rect(x, y, counterSizePX, counterSizePX).attr({ id: 'rect_' + sl.number, stroke: "black", "strokeWidth": 1, fill: backgroundColor, "data-type": "holder" })

                let colRowToDraw = "col " + (sl.rearPosition.col + 1) + " row " + (sl.rearPosition.row + 1)
                let counterNumberToDraw = sl.rearNumber + 1
                let sideText = 'REAR'
                if (!sheetSide || sheetSide === 'front') {
                    colRowToDraw = "col " + (sl.position.col + 1) + " row " + (sl.position.row + 1)
                    counterNumberToDraw = sl.number + 1
                    sideText = 'FRONT'
                }

                paper.text(cx, cy + (counterSizePX * 0.29), colRowToDraw).attr(
                    {
                        "textAnchor": "middle",
                        "dominant-baseline": "central",
                        "fontSize": counterSizePX * 0.14,
                        "fontWeight": "normal",
                        "fontFamily": "sans-serif",
                        stroke: "none",
                        fill: "#555",
                        "data-type": "holderText"
                    })


                paper.text(cx, cy, counterNumberToDraw + '').attr(
                    {
                        "textAnchor": "middle",
                        "dominant-baseline": "central",
                        "fontSize": counterSizePX / 4,
                        "fontWeight": "normal",
                        "fontFamily": "sans-serif",
                        stroke: "none",
                        fill: "#555",
                        "data-type": "holderText"
                    })

                paper.text(cx, cy - (counterSizePX * 0.29), sideText).attr(
                    {
                        "textAnchor": "middle",
                        "dominant-baseline": "central",
                        "fontSize": counterSizePX / 6,
                        "fontWeight": "normal",
                        "fontFamily": "sans-serif",
                        stroke: "none",
                        fill: "#555",
                        "data-type": "holderText"
                    })

            }

        })
    }

    const onEdit = evt => {
        if (sgContextMenuActive.value) {
            if (sgContextMenuActive.value.counterState) {
                // let frontAlv = JSON.parse(sgContextMenuActive.value.counterState.package.counterSideActive.front)
                // //console.log('slot edit frontAlv:', frontAlv, 'state.layers:', state.layers)
                // let rearAlv = JSON.parse(sgContextMenuActive.value.counterState.package.counterSideActive.rear)
                // let layersUsed = []
                // let layerKeys = []
                // for (const [key, value] of Object.entries(frontAlv)) {
                //     let split = key.split('_')
                //     let layerKey = Number(split[0])
                //     if (!layerKeys.includes(layerKey)) {
                //         layerKeys.push(layerKey)
                //     }
                // }
                // layerKeys.sort((a, b) => a - b)
                // layerKeys.forEach(layerKey => {
                //     //console.log('looking for layerKey:', layerKey, ' in state.layers:', state.layers)
                //     let layer = state.layers.find(l => l.layerKey === layerKey)
                //     if (layer) {
                //         let requiredValue = ''
                //         if (layer.layerActiveRequiredInputKey) {
                //             requiredValue = frontAlv[layer.layerKey + '_' + layer.layerActiveRequiredInputKey]
                //         }
                //         layersUsed.push({ layerKey: layer.layerKey, layerName: layer.layerName, requiredValue: requiredValue })
                //     }
                //     else {
                //         console.error('could not find layerKey:', layerKey, ' in state.layers:', state.layers)
                //     }
                // })
                // //console.log('layersUsed:', layersUsed)

                actions.counterLoadFromSheet({ ...sgContextMenuActive.value.counterState.package })
            }
            sgContextMenuActive.value = null
        }
    }

    const onDelete = evt => {
        let slots = [...state.slots]
        let slot = slots.find(ss => ss.number === sgContextMenuActive.value.number)
        if (slot) {
            slot.counterState = null
            actions.slots(slots)
        }
        sgContextMenuActive.value = null
    }

    const changeSheetParameters = sp => {
        if (sp) {
            setSheetParameters(sp)
        }
    }

    const changeCounterSizeMM = (cmm, custom = false) => {
        let useSheetParameters = { ...sheetParameters }
        useSheetParameters.counterSizeMM = cmm
        useSheetParameters.useCounterCustomSize = custom
        useSheetParameters.counterCustomSize = custom ? cmm : ''
        let _sheetParameters = computeSheetParameters(useSheetParameters)
        setSheetParameters(_sheetParameters)
    }

    const changeCounterMargins = margins => {
        let useSheetParameters = { ...sheetParameters }
        useSheetParameters.counterMargins = margins

        let _sheetParameters = computeSheetParameters(useSheetParameters)
        setSheetParameters(_sheetParameters)
    }

    const changePageMargins = margins => {
        let useSheetParameters = { ...sheetParameters }
        useSheetParameters.pageMargins = margins
        let _sheetParameters = computeSheetParameters(useSheetParameters)

        setSheetParameters(_sheetParameters)
    }

    const changeSheetMenuControlsOpened = opened => {
        setSheetMenuControlsOpened(opened)
    }
    const changeSheetMenuOperationsOpened = opened => {
        setSheetMenuOperationsOpened(opened)
    }
    const changeSheetMenuEditOpened = opened => {
        setSheetMenuEditOpened(opened)
        if (opened === false) {//xxx
            if (sheetEditPaper) {
                sheetEditPaper.clear()
                setHilitedSlots([])
                setHilitedSlotIndex(-1)
            }
        }
    }

    const changeGuideType = guideType => {

        if (guideType) {
            if (changingGuideType === guideType) {
                return
            }
            setChangingGuideType(guideType)
        }
    }

    useEffect(() => {
        if (changingGuideType) {
            setSheetParameters({ ...sheetParameters, guideType: changingGuideType })
        }
    }, [changingGuideType]) // eslint-disable-line react-hooks/exhaustive-deps

    const manageGuideType = () => {
        let guide = sheetParameters.guideType
        let guidesToShow = []
        let guidesToHide = []
        if (guide === 'none') {
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideCorners"]`))
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideEdges"]`))
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideLines"]`))
        }
        if (guide === 'corners') {
            guidesToShow = guidesToShow.concat(...document.querySelectorAll(`[data-type="cuttingGuideCorners"]`))
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideEdges"]`))
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideLines"]`))
        }
        if (guide === 'edges') {
            guidesToShow = guidesToShow.concat(...document.querySelectorAll(`[data-type="cuttingGuideEdges"]`))
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideCorners"]`))
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideLines"]`))
        }
        if (guide === 'lines') {
            guidesToShow = guidesToShow.concat(...document.querySelectorAll(`[data-type="cuttingGuideLines"]`))
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideCorners"]`))
            guidesToHide = guidesToHide.concat(...document.querySelectorAll(`[data-type="cuttingGuideEdges"]`))
        }
        guidesToHide.forEach(guide => guide ? guide.style.opacity = 0 : null)
        guidesToShow.forEach(guide => guide ? guide.style.opacity = 1 : null)
    }

    const changePrintableArea = area => {
        if (!area ||
            !Array.isArray(area) ||
            area.length !== 2 ||
            !Utility.isNumeric(area[0]) ||
            !Utility.isNumeric(area[1]) ||
            area[1] === 0) {
            return
        }
        let ratio = area[0] / area[1]
        let ele = document.getElementById('sheetDisplayContainer')
        if (ele) {
            ele.style.aspectRatio = ratio
            const sheetBbox = ele.getBoundingClientRect()
            let _sheetParameters = {
                ...sheetParameters,
                printableAreaMM: area, width: sheetBbox.width, height: sheetBbox.height
            }
            _sheetParameters = computeSheetParameters(_sheetParameters)
            setSheetParameters(_sheetParameters)
        }
    }

    useEffect(() => {
        if (sheetAspectRatioChangedSheetParameters) {
            setSheetParameters(sheetAspectRatioChangedSheetParameters)
        }
        setSheetAspectRatioChangedSheetParameters(false)
    }, [sheetAspectRatioChangedSheetParameters])

    useEffect(() => {

        if (state.loadSheet && state.loadSheet.sheetSettings && state.loadSheet.slots && state.loadSheet.slots.length > 0 && state.loadSheet.sheetSettings.guideType) {
            actions.sheetSettings(state.loadSheet.sheetSettings)
            actions.slots(state.loadSheet.slots)
            setRedoSheet(true)
        }
    }, [state.loadSheet]) // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        if (redoSheet) {
            if (state.sheetSettings && !Utility.emptyCheck(state.sheetSettings)) {
                setupSheetParameters()
                setRedoSheet(false)
            }
        }

    }, [redoSheet]) // eslint-disable-line react-hooks/exhaustive-deps

    const changeSheetSide = side => {
        if (side !== sheetSide) {
            setSheetSide(side)
        }
    }

    useEffect(() => {
        if (sheetSide === 'front' || sheetSide === 'rear') {
            if (sheetAlreadyCorrect() === false) {
                reverseSheet()
            }
        }
    }, [sheetSide]) // eslint-disable-line react-hooks/exhaustive-deps

    const sheetAlreadyCorrect = () => {
        let slotsOccupied = state.slots.filter(ss => ss.counterState !== null)
        if (slotsOccupied.length > 0) {
            let slot0 = slotsOccupied[0]
            if (slot0 && slot0.counterState.package && slot0.counterState.package.counterSideActive) {
                if (slot0.counterState.package.counterSideActive.active === sheetSide) {
                    return true
                }
            }
        }

        return false
    }

    const reverseSheet = () => {
        let targetSlots = JSON.parse(JSON.stringify(state.slots))
        targetSlots.filter(ts => ts.counterState = null)
        let slotsOccupied = state.slots.filter(ss => ss.counterState !== null)
        if (slotsOccupied.length === 0) {
            paintSheet()
            return
        }
        slotsOccupied.forEach(so => {
            if (sheetSide === 'rear') {
                let targetNumber = so.rearNumber
                let targetSlot = targetSlots.find(ts => ts.number === targetNumber)
                if (targetSlot) {
                    targetSlot.counterState = so.counterState
                    if (targetSlot.counterState && targetSlot.counterState.package) {
                        targetSlot.counterState.package.counterSideActive.active = 'rear'
                    }
                    else {
                        so.counterState = null
                    }
                }
            }
            if (sheetSide === 'front') {
                let targetNumber = so.number
                let targetSlot = targetSlots.find(ts => ts.rearNumber === targetNumber)
                if (targetSlot) {
                    targetSlot.counterState = so.counterState
                    if (targetSlot.counterState && targetSlot.counterState.package) {
                        targetSlot.counterState.package.counterSideActive.active = 'front'
                    }
                    else {
                        so.counterState = null
                    }
                }
            }
        })
        actions.slots(targetSlots)
    }

    const hiliteSlots = slotNumbers => {
        let _paper = sheetEditPaper
        if (sheetEditPaper) {
            sheetEditPaper.clear()
        }
        else {
            _paper = Snap("#sheetEditCover");
            _paper.attr({ id: "sheetEditCover", width: "100%", height: "100%" });
            setSheetEditPaper(_paper)
        }

        state.slots.forEach(slot => {
            if (!slotNumbers.includes(slot.number) && slot.xy.x > -1) {
                let x = slot.xy.x
                let y = slot.xy.y
                let counterSizePX = sheetParameters.counterSizeInPixels
                _paper.rect(x, y, counterSizePX, counterSizePX).attr({ id: 'slot_' + slot.number, stroke: "none", "strokeWidth": 0, fill: "black", "opacity": "0.8", "data-type": "holder" })
            }
        })

        setHilitedSlots(slotNumbers)
    }

    const moveToNext = nextSlot => {
        //console.log('moveToNext:', nextSlot)
    }

    const hiliteTargetSlotIndex = index => {
        if (sheetEditPaper) {
            let hilited = sheetEditPaper.selectAll(`[data-type="hilite"]`)
            hilited.forEach(hl => {
                if (hl) {
                    hl.remove()
                }
            })

            let slotNumber = hilitedSlots[index]
            let slot = state.slots.find(ss => ss.number === slotNumber)
            if (slot) {
                let x = slot.xy.x
                let y = slot.xy.y
                let counterSizePX = sheetParameters.counterSizeInPixels
                sheetEditPaper.rect(x - 1, y - 1, counterSizePX + 2, counterSizePX + 2).attr({ id: 'slot_' + slot.number + '_selected', stroke: "yellow", "strokeWidth": 2, fill: "none", "data-type": "hilite" })
                sheetEditPaper.rect(x - 2, y - 2, counterSizePX + 4, counterSizePX + 4).attr({ id: 'slot_' + slot.number + '_selected2', stroke: "cyan", "strokeWidth": 2, fill: "none", "data-type": "hilite" })
                setHilitedSlotIndex(index)
            }
        }
    }

    return <div className="sheet">
        {sheetParameters ?
            <div className={!sheetMenuControlsOpened &&
                !sheetMenuOperationsOpened &&
                !sheetMenuEditOpened ? "sheet-menus all-closed" :

                sheetMenuControlsOpened && !sheetMenuOperationsOpened &&
                    !sheetMenuEditOpened ? "sheet-menus first-opened-only" :


                    !sheetMenuControlsOpened && sheetMenuOperationsOpened &&
                        !sheetMenuEditOpened ? "sheet-menus second-opened-only" :


                        !sheetMenuControlsOpened && !sheetMenuOperationsOpened &&
                            sheetMenuEditOpened ? "sheet-menus third-opened-only" :
                            "sheet-menus"


            }>
                <div>
                    <SheetControls sheetParameters={sheetParameters}
                        changeSheetParameters={changeSheetParameters}
                        changeCounterSizeMM={changeCounterSizeMM}
                        changeCounterMargins={changeCounterMargins}
                        changePageMargins={changePageMargins}
                        changeSheetMenuControlsOpened={changeSheetMenuControlsOpened}
                        changeSheetSide={changeSheetSide}
                    />
                </div>
                <div>
                    <SheetOperations sheetParameters={sheetParameters}
                        changeGuideType={changeGuideType}
                        changeSheetMenuOperationsOpened={changeSheetMenuOperationsOpened}
                        changePrintableArea={changePrintableArea}
                    />
                </div>
                <div>
                    <SheetEdit
                        changeSheetMenuEditOpened={changeSheetMenuEditOpened}
                        hiliteSlots={hiliteSlots}
                        hiliteTargetSlotIndex={hiliteTargetSlotIndex}
                        moveToNext={moveToNext}
                    />
                </div>
            </div>
            : ''}
        <div id="sheetContainer" className="sheet-container">

            <div id="sheetDisplayContainer" ref={sheetDisplayRef} className="sheet-display"><svg ref={svgRef} id="sheetDisplay" className="sheet-display-svg" />
                {sgContextMenuActive.value ?
                    <div style={{ left: `${contextBox.left}`, top: `${contextBox.top}` }} id="sheet_context_menu" onClick={evt => evt.preventDefault()} className="context-menu">
                        <div id="sheet_context_menu2">
                            <button id="sheet_context_menuEdit" className="standard-button blue" onClick={onEdit}>edit</button>
                            <button id="sheet_context_menuDelete" className="standard-button red" onClick={onDelete}>delete</button>
                        </div>
                    </div>
                    : ''}
                <div className={sheetMenuEditOpened ? 'sheet-edit-cover' : 'display-none'}>
                    <svg id="sheetEditCover" className="sheet-edit-cover-svg" />
                </div>
            </div>

        </div>

    </div>
}
export default Sheet;